import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DataTableSort } from '@/components/ui';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { PopupAdminView } from '../../types/cms';
import type {
  PopupConfigRequest,
  PopupReservationAdminFilter,
  PopupReservationAdminResult,
  PopupReservationAdminView,
  PopupReservationPagination,
  PopupSlotCopyRequest,
  PopupSlotCreateRequest,
  PopupSlotUpdateRequest,
  PopupSlotView,
} from '../types/popupDetail';

const base = (popupId: string) => `/admin/v1/popups/${popupId}`;

interface Envelope<T> {
  success?: boolean;
  data?: T | null;
  meta?: { pagination?: PopupReservationPagination } | null;
}

// ---------- config ----------

export const usePopupConfigMutation = (popupId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PopupConfigRequest): Promise<PopupAdminView> => {
      const res = await adminFetch(`${base(popupId)}/config`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`popup config update failed: ${res.status}`);
      const env = (await res.json()) as Envelope<PopupAdminView>;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'popup', popupId] });
    },
  });
};

// ---------- slots ----------

export const popupSlotsQueryKey = (popupId: string, slotDate?: string) =>
  ['admin', 'popup-slots', popupId, slotDate ?? ''] as const;

const fetchSlots = async (popupId: string, slotDate?: string): Promise<PopupSlotView[]> => {
  const qs = slotDate ? `?slotDate=${encodeURIComponent(slotDate)}` : '';
  const res = await adminFetch(`${base(popupId)}/slots${qs}`);
  if (!res.ok) throw new Error(`popup slots load failed: ${res.status}`);
  const env = (await res.json()) as Envelope<PopupSlotView[]>;
  return env.data ?? [];
};

export const usePopupSlotsQuery = (popupId: string, slotDate?: string) =>
  useQuery<PopupSlotView[]>({
    queryKey: popupSlotsQueryKey(popupId, slotDate),
    queryFn: () => fetchSlots(popupId, slotDate),
    enabled: !!popupId && popupId !== 'new',
  });

export const usePopupSlotMutations = (popupId: string) => {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin', 'popup-slots', popupId] });

  const create = useMutation({
    mutationFn: async (body: PopupSlotCreateRequest): Promise<PopupSlotView> => {
      const res = await adminFetch(`${base(popupId)}/slots`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`popup slot create failed: ${res.status}`);
      const env = (await res.json()) as Envelope<PopupSlotView>;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidate,
  });

  // ID36: 슬롯 사후 수정. PUT /admin/v1/popups/{popupId}/slots/{slotId}.
  // Idempotency-Key 필수(writeAllowlist 등록). 5분 단위/현재예약 미만 정원은 BE 가 400.
  const update = useMutation({
    mutationFn: async ({
      slotId,
      body,
    }: {
      slotId: number;
      body: PopupSlotUpdateRequest;
    }): Promise<PopupSlotView> => {
      const path = `${base(popupId)}/slots/${slotId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`popup slot update failed: ${res.status}`);
      const env = (await res.json()) as Envelope<PopupSlotView>;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (slotId: number): Promise<void> => {
      const res = await adminFetch(`${base(popupId)}/slots/${slotId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`popup slot delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  // QA-181(V109): 회차 복사. POST /slots/copy ({sourceDate, targetDates[]}) — 원자적.
  const copy = useMutation({
    mutationFn: async (body: PopupSlotCopyRequest): Promise<void> => {
      const path = `${base(popupId)}/slots/copy`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`popup slot copy failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  // QA-181(V109): 슬롯 엑셀 일괄 업로드. ★be-o 정본 — POST /slots/bulk = **multipart/form-data**
  // `@RequestPart("file")` 로 .xlsx 파일을 받아 **서버(POI)가 파싱**(BO 클라 파싱 없음). 5분단위 등
  // 전건 검증 후 원자적 생성. 상품 import(/products/import)와 동형.
  // Content-Type 은 브라우저가 boundary 와 함께 설정해야 하는데, body 가 FormData 면 adminFetch 가
  // 알아서 빼준다(인증은 유지) — 호출부에서 헤더를 직접 지우던 처리를 래퍼로 옮겼다.
  const bulkUpload = useMutation({
    mutationFn: async (file: File): Promise<void> => {
      const form = new FormData();
      form.append('file', file);
      const res = await adminFetch(`${base(popupId)}/slots/bulk`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) throw new Error(`popup slot bulk upload failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove, copy, bulkUpload };
};

// ---------- reservations (admin) ----------

// QA-184(V109): 필터를 캐시 키에 포함(필터 변경 시 재조회). 빈 값은 키에서 제외해 안정화.
// QA-184(서버 정렬): sort 도 캐시 키에 포함 — 정렬 변경 시 서버 재조회되도록.
export const popupReservationsAdminQueryKey = (
  popupId: string,
  page: number,
  filter?: PopupReservationAdminFilter,
  sort?: DataTableSort | null,
) => ['admin', 'popup-reservations-admin', popupId, page, filter ?? {}, sort ?? null] as const;

/**
 * QA-184(서버 정렬): DataTableSort(컬럼 id + 방향) → BE 엔티티 정렬 필드 매핑.
 * AdminPopupOpsController.listReservations 가 Pageable.sort 를 지원하므로 sort=field,dir 로 전송한다.
 * popupName 등 엔티티 비매핑 컬럼은 null 반환 → 호출부가 기본 정렬(createdAt,desc)로 폴백.
 */
const toReservationSortParam = (sort?: DataTableSort | null): string => {
  if (!sort) return 'createdAt,desc';
  const dir = sort.direction;
  switch (sort.key) {
    case 'reserver':
      return `reserverName,${dir}`;
    case 'slot':
      return `slotDate,${dir}&sort=slotTime,${dir}`;
    case 'peopleCount':
      return `peopleCount,${dir}`;
    case 'status':
      return `status,${dir}`;
    case 'enteredAt':
      return `enteredAt,${dir}`;
    case 'reservationNo':
      return `reservationId,${dir}`;
    default:
      // popupName 등 엔티티 비매핑 컬럼은 서버 정렬 불가 → 기본 정렬.
      return 'createdAt,desc';
  }
};

/** QA-184(V109): 필터 → query string. 빈 값은 생략(전체 조회). 공용(목록/export). */
const buildReservationFilterQuery = (
  qs: URLSearchParams,
  filter?: PopupReservationAdminFilter,
): void => {
  if (!filter) return;
  if (filter.status) qs.set('status', filter.status);
  const keyword = filter.keyword?.trim();
  if (keyword) qs.set('keyword', keyword);
  if (filter.usedFrom) qs.set('usedFrom', filter.usedFrom);
  if (filter.usedTo) qs.set('usedTo', filter.usedTo);
};

const fetchReservationsAdmin = async (
  popupId: string,
  page: number,
  size: number,
  filter?: PopupReservationAdminFilter,
  sort?: DataTableSort | null,
): Promise<PopupReservationAdminResult> => {
  // 요청은 0-based Pageable, 응답 meta.pagination.page 는 1-based.
  const qs = new URLSearchParams({
    page: String(Math.max(0, page - 1)),
    size: String(size),
  });
  buildReservationFilterQuery(qs, filter);
  // QA-184(서버 정렬): sort 는 'field,dir' 형식이며 slot 정렬은 'field,dir&sort=field,dir' 처럼
  // 다중 sort 파라미터를 포함하므로 URLSearchParams 인코딩을 거치지 않고 직접 덧붙인다.
  const sortParam = `sort=${toReservationSortParam(sort)}`;
  const res = await adminFetch(`${base(popupId)}/reservations/admin?${qs}&${sortParam}`);
  if (!res.ok) throw new Error(`popup reservations(admin) load failed: ${res.status}`);
  const env = (await res.json()) as Envelope<PopupReservationAdminView[]>;
  const items = env.data ?? [];
  const pagination: PopupReservationPagination = env.meta?.pagination ?? {
    page,
    size,
    totalItems: items.length,
    totalPages: 1,
  };
  return { items, pagination };
};

export const usePopupReservationsAdminQuery = (
  popupId: string,
  page: number,
  size: number,
  filter?: PopupReservationAdminFilter,
  sort?: DataTableSort | null,
) =>
  useQuery<PopupReservationAdminResult>({
    queryKey: popupReservationsAdminQueryKey(popupId, page, filter, sort),
    queryFn: () => fetchReservationsAdmin(popupId, page, size, filter, sort),
    enabled: !!popupId,
  });

// QA-184(V109): 예약자 엑셀 다운로드. ★be-o 정본 — GET /reservations/export = **서버 xlsx blob**
// (ResponseEntity<byte[]>, 주문 export 와 동형). BO 는 blob 받아 앵커 다운로드(클라 CSV 빌드 없음).
// 컬럼=이용일시(slotDate+slotTime)/예약일시/고객명/연락처/예약상태(statusLabel)/예약번호(R{id})/프로젝트명,
// raw enum 미노출은 BE 처리. 현재 필터 그대로 전송해 화면 결과와 1:1. 503 등은 status 그대로 throw.
export const downloadPopupReservationsExport = async (
  popupId: string,
  filter?: PopupReservationAdminFilter,
): Promise<void> => {
  const qs = new URLSearchParams();
  buildReservationFilterQuery(qs, filter);
  const query = qs.toString();
  const res = await adminFetch(
    `${base(popupId)}/reservations/export${query ? `?${query}` : ''}`,
  );
  if (!res.ok) throw new Error(`popup reservations export failed: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `popup-reservations-${popupId}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

const reservationAction = async (
  popupId: string,
  reservationId: number,
  // QA-184(V109): 'use'='이용 완료'(POST /reservations/{id}/use).
  action: 'qr-regenerate' | 'refund' | 'no-show' | 'use',
): Promise<PopupReservationAdminView> => {
  const res = await adminFetch(`${base(popupId)}/reservations/${reservationId}/${action}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`reservation ${action} failed: ${res.status}`);
  const env = (await res.json()) as Envelope<PopupReservationAdminView>;
  if (!env.data) throw new Error('no data');
  return env.data;
};

export const usePopupReservationActions = (popupId: string) => {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin', 'popup-reservations-admin', popupId] });

  const regenerateQr = useMutation({
    mutationFn: (reservationId: number) =>
      reservationAction(popupId, reservationId, 'qr-regenerate'),
    onSuccess: invalidate,
  });

  const refund = useMutation({
    mutationFn: (reservationId: number) =>
      reservationAction(popupId, reservationId, 'refund'),
    onSuccess: invalidate,
  });

  const noShow = useMutation({
    mutationFn: (reservationId: number) =>
      reservationAction(popupId, reservationId, 'no-show'),
    onSuccess: invalidate,
  });

  // QA-184(V109): '이용 완료' 처리.
  const markUsed = useMutation({
    mutationFn: (reservationId: number) =>
      reservationAction(popupId, reservationId, 'use'),
    onSuccess: invalidate,
  });

  return { regenerateQr, refund, noShow, markUsed };
};

// ---------- QR verify (수동 토큰 입력) ----------

export class QrVerifyError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'QrVerifyError';
  }
}

export const usePopupQrVerifyMutation = (popupId: string) => {
  const qc = useQueryClient();
  return useMutation<PopupReservationAdminView, QrVerifyError, string>({
    mutationFn: async (qrToken: string) => {
      const res = await adminFetch(`${base(popupId)}/qr-verify`, {
        method: 'POST',
        body: JSON.stringify({ qrToken }),
      });
      if (!res.ok) throw new QrVerifyError(`qr verify failed: ${res.status}`, res.status);
      const env = (await res.json()) as Envelope<PopupReservationAdminView>;
      if (!env.data) throw new QrVerifyError('no data', res.status);
      return env.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'popup-reservations-admin', popupId] });
    },
  });
};
